// public/js/features/manual/manual-drawings.js
// Proper dimensioned drawings for the device manual, drawn to scale from the
// manufacturer datasheets.
//
// WHY redrawn rather than cropped out of the PDF. The datasheets do carry
// mechanical drawings, but they are embedded as small JPEGs (the Blue Puck one
// is 520x443 with compression fringing around every line), they sit on a fixed
// white background that inverts badly in dark mode, they are the supplier's
// artwork, and they are captioned with the VENDOR model name - which customers
// must never see (docs/device-manual.md, branded-models.js). Redrawing to the
// same dimensions keeps the information, loses all four problems, and scales.
//
// Units are millimetres: the viewBox IS the part, so a dimension in the drawing
// is the dimension in the datasheet and there is no scale factor to get wrong.
// Everything strokes in `currentColor` so the drawing follows the theme.
//
// Only dimensions we can read unambiguously off the datasheet are drawn. The
// Blue Puck sheet also carries a 5.50 on a narrow auxiliary view whose datum is
// not clear; a wrong label on a drawing is worse than a missing one, so it is
// left out rather than guessed.

const S = {                       // one place for the drawing conventions
  outline: 'fill="none" stroke="currentColor" stroke-width="0.7"',
  thin:    'fill="none" stroke="currentColor" stroke-width="0.4" opacity="0.85"',
  centre:  'fill="none" stroke="currentColor" stroke-width="0.3" stroke-dasharray="5 1.5 1 1.5" opacity="0.45"',
  dim:     'fill="none" stroke="currentColor" stroke-width="0.3" opacity="0.7"',
  label:   'fill="currentColor" font-size="4" font-family="system-ui, sans-serif"',
};

// Dimension line with arrowheads at both ends; the label sits centred on it,
// rotated upright for a vertical run (drawing convention).
function dim(x1, y1, x2, y2, text, { off = 2.6 } = {}) {
  const vertical = x1 === x2;
  const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
  const tx = vertical ? mx - off : mx;
  const ty = vertical ? my : my - off;
  const rot = vertical ? ` transform="rotate(-90 ${tx} ${ty})"` : '';
  return `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" ${S.dim}`
    + ` marker-start="url(#mdArrow)" marker-end="url(#mdArrow)"/>`
    + `<text x="${tx}" y="${ty}" text-anchor="middle" ${S.label}${rot}>${text}</text>`;
}

// Witness line: the thin extension a dimension is measured between.
const ext = (x1, y1, x2, y2) => `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" ${S.dim}/>`;

const defs = `<defs>
  <marker id="mdArrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6"
          orient="auto-start-reverse" markerUnits="strokeWidth">
    <path d="M 0 2.5 L 10 5 L 0 7.5 z" fill="currentColor" opacity="0.7"/>
  </marker>
</defs>`;

/**
 * Blue Puck style disc sensor - the housing behind Temp+ and Humidity+.
 * Datasheet: ELA Innovation product sheet IDF25242D (FP Blue PUCK RHT 07B EN).
 *
 *   top view  : D57 base, D40 dome, 48 across the two moulded side slots,
 *               central fixing hole D6 counterbored D10.50, 13 below centre
 *   side view : 20 mm overall height, aligned under the top view
 */
function discPuck() {
  const CX = 50, CY = 40;                 // top-view centre
  const R = 28.5, RD = 20, HALF = 24;     // D57 / D40 / 48 across the slots
  const HX = CX, HY = CY + 13;            // fixing hole, 13 below centre
  const SY = 101, H = 20;                 // side view: baseline and height

  // The two moulded slots, between dome and rim, left and right.
  const slot = (sign) => {
    const x = CX + sign * HALF;
    return `<path d="M ${x} ${CY - 9} A 25.5 25.5 0 0 ${sign > 0 ? 1 : 0} ${x} ${CY + 9}" ${S.outline}/>`;
  };

  const top = `
    <circle cx="${CX}" cy="${CY}" r="${R}" ${S.outline}/>
    <circle cx="${CX}" cy="${CY}" r="${RD}" ${S.thin}/>
    ${slot(1)}${slot(-1)}
    <circle cx="${HX}" cy="${HY}" r="5.25" ${S.thin}/>
    <circle cx="${HX}" cy="${HY}" r="3" ${S.outline}/>
    <line x1="${CX - R - 5}" y1="${CY}" x2="${CX + R + 5}" y2="${CY}" ${S.centre}/>
    <line x1="${CX}" y1="${CY - R - 5}" x2="${CX}" y2="${CY + R + 5}" ${S.centre}/>
    <line x1="${HX - 8}" y1="${HY}" x2="${HX + 8}" y2="${HY}" ${S.centre}/>

    ${/* D57 outermost, D40 inside it - larger dimension further from the part */ ''}
    ${ext(CX - R, CY - R, 6, CY - R)}
    ${ext(CX - R, CY + R, 6, CY + R)}
    ${dim(9, CY - R, 9, CY + R, 'Ø 57')}
    ${ext(CX - RD, CY - RD, 19, CY - RD)}
    ${ext(CX - RD, CY + RD, 19, CY + RD)}
    ${dim(22, CY - RD, 22, CY + RD, 'Ø 40')}

    ${ext(CX - HALF, CY - 20, CX - HALF, 8)}
    ${ext(CX + HALF, CY - 20, CX + HALF, 8)}
    ${dim(CX - HALF, 10, CX + HALF, 10, '48')}

    ${dim(CX + 10, CY, CX + 10, HY, '13', { off: -3 })}
    <line x1="${HX + 5}" y1="${HY + 2.5}" x2="${HX + 19}" y2="${HY + 9}" ${S.dim}/>
    <text x="${HX + 20}" y="${HY + 10.5}" ${S.label}>Ø 10.50 / Ø 6</text>`;

  // Side profile: flat base D57, shoulder, then the shallow D40 dome.
  const side = `
    <path d="M ${CX - R} ${SY}
             L ${CX - R} ${SY - 4.5}
             L ${CX - RD - 3} ${SY - 8}
             Q ${CX - RD + 1} ${SY - H} ${CX - 11} ${SY - H}
             L ${CX + 11} ${SY - H}
             Q ${CX + RD - 1} ${SY - H} ${CX + RD + 3} ${SY - 8}
             L ${CX + R} ${SY - 4.5}
             L ${CX + R} ${SY} Z" ${S.outline}/>
    <line x1="${CX - R}" y1="${SY - 4.5}" x2="${CX + R}" y2="${SY - 4.5}" ${S.thin}/>
    <line x1="${CX}" y1="${SY - H - 4}" x2="${CX}" y2="${SY + 4}" ${S.centre}/>
    ${ext(CX - R, SY, 6, SY)}
    ${ext(CX - 11, SY - H, 6, SY - H)}
    ${dim(9, SY - H, 9, SY, '20')}`;

  return `<svg viewBox="0 2 122 118" width="100%" style="max-width:430px;" role="img"
               aria-label="Dimensioned drawing: 57 mm diameter disc, 20 mm high, with a central 6 mm fixing hole 13 mm off centre">
    ${defs}${top}${side}
    <text x="${CX}" y="${SY + 9}" text-anchor="middle" ${S.label} opacity="0.6">Dimensions in mm</text>
  </svg>`;
}

// Registry: a manual entry opts in with `drawing: '<key>'` in manual-data.js.
// Anything without one keeps the generic sketch in manual-page.js.
export const DRAWINGS = {
  discPuck,
};

export function deviceDrawing(key) {
  const fn = key && DRAWINGS[key];
  return fn ? fn() : '';
}
